import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@companion-professional/webutils';

// This directory contains the styled components for a simple card (a container with a header, content, and footer).
//
// The common structure of the card is:
//   <Card>
//     <CardHeader>
//       <CardTitle>...</CardTitle>
//       <CardDescription>...</CardDescription>
//     </CardHeader>
//     <CardContent>...</CardContent>
//     <CardFooter>...</CardFooter>
//   </Card>

export { CardContent } from './CardContent';
export { CardDescription } from './CardDescription';
export { CardFooter } from './CardFooter';
export { CardHeader } from './CardHeader';
export { CardTitle } from './CardTitle';

export const cardVariants = cva(['flex', 'flex-col', 'justify-between', 'rounded-xl', 'border', 'border-border'], {
  variants: {
    variant: {
      // default is a white surface that floats on the page background.
      default: ['bg-widget', 'text-on-widget', 'shadow-card'],
      // muted is a flat, subtle panel for supporting content inside another surface (e.g. status messages in a
      // Section).
      muted: ['bg-muted', 'text-on-background']
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant, ...props }, ref) => (
  <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
));
Card.displayName = 'Card';
